import { useEffect, useState } from "react";
import { useSimpleRequest } from "hooks";
import { compose } from 'infrastructure/store';
import { Paginate } from "consts/table";
import { Filters } from "consts/onboarding";
import { RequestQueryMapperStrategy, SortOrder } from "consts/request-query";
import { when } from "util/support";
import { RequestQueryMapper } from "util/request-query-mapper";
import type { OnboardingApplicationStepAndFieldHistoryLogEntry } from "features/onboarding/types";
import { getOnboardingLogs } from "features/onboarding/api";
import type {
    OnboardingApplicationLogsPaginator,
    OnboardingApplicationStepAndFieldHistoryLogEntryWithPreviousChanges
} from "../types";
import {
    addUniqueId,
    attachPreviousChanges,
    parseChanges,
    sortByUpdatedDateTime,
    withGroupByResource
} from '../middleware';
import { useApplicationTableResourceContext } from "../../../ApplicationTableResourceProvider";

export const useOnboardingApplicationLogsRequest = () => {
    const [loading, setLoading] = useState(false);

    const [applicationLogs, setApplicationLogs] = useState<OnboardingApplicationLogsPaginator<
        Record<
            string,
            OnboardingApplicationStepAndFieldHistoryLogEntryWithPreviousChanges[]
        >
    >>();

    const { open } = useApplicationTableResourceContext();

    const request = useSimpleRequest({ setLoading });

    const fetchOnboardingLogs = ({
        applicationId,
        resource
    }: Partial<Pick<OnboardingApplicationStepAndFieldHistoryLogEntry, 'applicationId' | 'resource'>>,
        queryParams?: string
    ) =>
        request(() => {
            const searchQueryParams = RequestQueryMapper.from(queryParams, {
                setStrategy: RequestQueryMapperStrategy.Set
            })
                .contains(Filters.applicationId, `${applicationId}`);

            return getOnboardingLogs(
                when(
                    resource,
                    () => searchQueryParams
                        .contains(Filters.resource, `${resource}`),

                    () => searchQueryParams
                )
                    .contains(Paginate.perPage, `${Number.MAX_SAFE_INTEGER}`)
                    .toString()
            )
        }, {
            onSuccess: ({ data }) => (
                setApplicationLogs(
                    compose<OnboardingApplicationLogsPaginator<
                        Record<
                            string,
                            OnboardingApplicationStepAndFieldHistoryLogEntryWithPreviousChanges[]
                        >
                    >>(
                        parseChanges,
                        attachPreviousChanges,
                        addUniqueId,
                        sortByUpdatedDateTime(SortOrder.Asc),
                        withGroupByResource
                    )(data)
                )
            )
        });

    const fetchOnboardingLogsIfNotExists = (
        params: Parameters<typeof fetchOnboardingLogs>[0],
        canFetch: () => boolean
    ) => when(
        () => (
            !applicationLogs &&
            params.applicationId &&
            canFetch()
        ),
        () => fetchOnboardingLogs(params)
    );

    useEffect(() => {
        if (!open) {
            setApplicationLogs(undefined);
        }
    }, [open]);

    return {
        loading,
        toRows: () => Object.values(applicationLogs?.data ?? {}).flat(),
        fetchOnboardingLogs,
        fetchOnboardingLogsIfNotExists
    };
};

export default useOnboardingApplicationLogsRequest;
